import { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Video, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { emitRecordingChange } from '../../lib/rec/store';
import type { InboxResponse, RecordingDTO } from '../../lib/rec/types';
import { useRec } from './RecProvider';

/**
 * BANNER "NUEVO VIDEO" (pedido del dueño 2026-09-10: "este le salga arriba en el banner como algo
 * de new video ... para que ellos le den click y lo vean embebido"). Vive en el stack `.top-banners`
 * (z 500), patrón de `InspectorAlertBanner`: poll de `GET /api/recordings/inbox` (solo lectura: los
 * recibidos por mí, `ready`, SIN ver y SIN descartar), "Ver" abre el visor EMBEBIDO en el centro
 * (no navega a /rec ni a Messaging), y ✕ descarta por-usuario (`POST /:id/dismiss-banner`) para que
 * no vuelva a salir. Verlo también lo quita (queda registrado en `screen_recording_views`).
 *
 * Pausa con la pestaña oculta (como los demás contadores). Publica el conteo por un evento para el
 * badge del ítem de nav de AppShell (una sola fuente de verdad, este poll).
 */
const POLL_MS = 45_000;

export function NewVideoBanner() {
  const { t } = useI18n();
  const rec = useRec();
  const [rows, setRows] = useState<RecordingDTO[]>([]);

  const publish = useCallback((n: number) => {
    try { window.dispatchEvent(new CustomEvent('recordings:inbox', { detail: n })); } catch { /* */ }
  }, []);

  const sinAcceso = useRef(false);
  const load = useCallback(async () => {
    if (sinAcceso.current) return;
    try {
      const r = await api.get<InboxResponse>('/api/recordings/inbox');
      setRows(r.rows);
      // El badge del nav es `unseenCount` (todo lo recibido SIN ver), NO rows.length: cerrar un
      // banner (descarte) saca la fila pero no baja el badge — todavía no lo viste. Solo verlo lo baja
      // (marca la vista → el próximo poll trae un unseenCount menor).
      publish(r.unseenCount ?? r.rows.length);
    } catch (e) {
      // Sin permiso (403) o módulo apagado (404): no tiene sentido volver a preguntar cada 45 s
      // (auditoría 2026-09-15: 58 respuestas 403 en una sola sesión de capturas).
      const st = (e as { status?: number }).status;
      if (st === 403 || st === 404) sinAcceso.current = true;
    }
  }, [publish]);

  useEffect(() => {
    void load();
    const iv = window.setInterval(() => { if (!document.hidden) void load(); }, POLL_MS);
    const onVis = () => { if (!document.hidden) void load(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    // Cuando algo cambia una grabación (verla, borrarla) re-consultamos para bajar el banner.
    window.addEventListener('recordings:changed', onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
      window.removeEventListener('recordings:changed', onVis);
    };
  }, [load]);

  // Sacar la fila del banner (verla o descartarla) NO recalcula el badge desde rows.length: el badge
  // es `unseenCount` y solo baja cuando la grabación queda vista (lo reconcilia el próximo poll de
  // /inbox). Descartar el banner deja el badge donde estaba.
  const quitar = useCallback((id: number) => {
    setRows((rs) => rs.filter((x) => x.id !== id));
  }, []);

  if (!rows.length) return null;

  const ver = (r: RecordingDTO) => {
    // Verlo lo saca del inbox (marca `viewed` al reproducir); lo quitamos ya para que el banner baje.
    rec.openViewer(r.id);
    quitar(r.id);
  };
  const dismiss = async (id: number) => {
    quitar(id); // optimista
    try { await api.post(`/api/recordings/${id}/dismiss-banner`, {}); } catch { /* reaparece en el próximo poll */ }
  };

  return (
    <div className="rec-newbanner" role="alert">
      {rows.map((r) => (
        <div key={r.id} className="rec-newbanner-row">
          <span className="rec-newbanner-ico"><Video size={15} strokeWidth={2} aria-hidden /></span>
          <span className="rec-newbanner-msg">{t('rec.newVideo', { name: r.owner.name, title: r.title })}</span>
          <button type="button" className="rec-newbanner-watch" onClick={() => ver(r)}>
            <Play size={12} strokeWidth={2.2} fill="currentColor" style={{ verticalAlign: 'text-bottom' }} /> {t('rec.watch')}
          </button>
          <button type="button" className="rec-newbanner-x" onClick={() => { void dismiss(r.id); emitRecordingChange(r.id, null); }} title={t('rec.bannerDismiss')} aria-label={t('rec.bannerDismiss')}>
            <X size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
          </button>
        </div>
      ))}
    </div>
  );
}
